import { CheckCircle, Warning, Error, Security } from '@mui/icons-material';
import { Box, Typography, LinearProgress, Alert, CircularProgress } from '@mui/material';
import React, { useState, useEffect } from 'react';

import { debouncedBreachCheck } from '../../utils/passwordBreachCheck';
import { validatePasswordStrength, getPasswordStrengthLabel } from '../../utils/passwordValidation';

interface PasswordStrengthIndicatorProps {
  password: string;
  showRequirements?: boolean;
  id?: string;
}

interface PasswordRequirement {
  label: string;
  met: boolean;
  regex: RegExp;
}

const getPasswordStrength = (password: string, breached?: boolean): { score: number; label: string; color: string } => {
  if (!password) return { score: 0, label: 'No password', color: 'error' };

  // If password is breached, maximum score is 25 regardless of other criteria
  if (breached) return { score: 25, label: 'Breached', color: 'error' };

  const validation = validatePasswordStrength(password);
  const strengthInfo = getPasswordStrengthLabel(validation.score);

  return {
    score: validation.score,
    label: strengthInfo.label,
    color: strengthInfo.color
  };
};

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  showRequirements = true,
  id
}) => {
  const [breachCheckResult, setBreachCheckResult] = useState<{ breached: boolean; count?: number; error?: string } | null>(null);
  const [isCheckingBreach, setIsCheckingBreach] = useState(false);

  // Check for password breaches when password meets minimum requirements
  useEffect(() => {
    if (password && password.length >= 8) {
      setIsCheckingBreach(true);
      debouncedBreachCheck(password, (result) => {
        setBreachCheckResult(result);
        setIsCheckingBreach(false);
      });
    } else {
      setBreachCheckResult(null);
      setIsCheckingBreach(false);
    }
  }, [password]);
  const strength = getPasswordStrength(password, breachCheckResult?.breached);
  const validation = validatePasswordStrength(password);

  if (!password) return null;

  return (
    <Box sx={{ mt: 1 }} id={id}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" sx={{ mr: 1 }}>
          Password Strength:
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: `${strength.color}.main`,
            fontWeight: 'bold'
          }}
        >
          {strength.label}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={strength.score}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: 'grey.300',
          '& .MuiLinearProgress-bar': {
            backgroundColor: `${strength.color}.main`,
            borderRadius: 4
          }
        }}
      />

      {/* Password Breach Warning */}
      {breachCheckResult?.breached && (
        <Alert
          severity="error"
          icon={<Security />}
          sx={{ mt: 1, mb: 1 }}
          role="alert"
          aria-live="polite"
        >
          <Typography variant="body2" fontWeight="bold">
            ⚠️ Password Breach Detected
          </Typography>
          <Typography variant="body2">
            This password has been found in {breachCheckResult.count?.toLocaleString() || 'multiple'} data breaches.
            Please choose a different password for your security.
          </Typography>
        </Alert>
      )}

      {/* Breach Check Loading */}
      {isCheckingBreach && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 1 }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Checking password security...
          </Typography>
        </Box>
      )}

      {showRequirements && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Security Issues:
          </Typography>
          {validation.issues.length > 0 ? (
            validation.issues.map((issue, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 0.5
                }}
              >
                <Warning sx={{ color: 'warning.main', mr: 1, fontSize: 16 }} />
                <Typography variant="body2" sx={{ color: 'warning.main' }}>
                  {issue}
                </Typography>
              </Box>
            ))
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 16 }} />
              <Typography variant="body2" sx={{ color: 'success.main' }}>
                No security issues detected
              </Typography>
            </Box>
          )}

          {validation.suggestions.length > 0 && (
            <>
              <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                Suggestions:
              </Typography>
              {validation.suggestions.map((suggestion, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 0.5
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    • {suggestion}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PasswordStrengthIndicator;